import { render } from '@testing-library/react';
import { ThemeProvider } from 'styled-components';
import { createTheme } from '@hiiretail/synergy-ui';
import { DispatchWithoutAction, ReactNode } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClient, QueryClientProvider } from 'react-query';
import { NotificationsProvider } from '../notifications/Notifications';
import { AuthContext } from '@hiiretail/core';
import firebase from 'firebase/app';
import {
  AuthContextType,
  LoginWithEmailAndPasswordDispatchAction,
  LoginWithProviderDispatchAction,
} from '@hiiretail/core/dist/types/auth/Auth.types';
import { BreadcrumbsActionsProvider } from '../components/Breadcrumbs/context/BreadcrumbsActionsContext';
import { unmountComponentAtNode } from 'react-dom';
import messages from '../../i18n';

export const renderWithProviders = (component: ReactNode): void => {
  const theme = createTheme();
  const queryClient = new QueryClient();
  render(
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <IntlProvider locale="en" messages={messages.en}>
          <NotificationsProvider>{component}</NotificationsProvider>
        </IntlProvider>
      </QueryClientProvider>
    </ThemeProvider>,
  );
};

export type MockAuth = {
  app: firebase.app.App;
  state: {
    currentUser: firebase.User | null;
    permissions: string[];
    roles: string[];
  };
  logout: DispatchWithoutAction;
  login: LoginWithEmailAndPasswordDispatchAction;
  loginViaProvider: LoginWithProviderDispatchAction;
  isLoading: boolean;
};

export const renderWithProvidersAndAuth = (component: ReactNode, authMock: MockAuth): void => {
  const theme = createTheme();
  const queryClient = new QueryClient();

  const newAuth: AuthContextType = {
    firebaseApp: authMock.app,
    currentUser: authMock.state?.currentUser,
    authInfo: {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      source: {} as any,
      requester: 'mockRequester',
      tenantId: 'mockTenantId',
      data: {
        fb: {
          tenantId: 'mockTenantId',
        },
        externalClaims: {
          roles: [],
          groups: [],
        },

        user: {
          id: 'mockId',
          type: 'shopper',
          isTenantAdmin: false,
          picture: 'mockPicture',
          groups: [],
        },
      },
    },
    permissions: authMock.state?.permissions,
    isInitialized: true,
    loading: {
      isProcessingAuthInfo: false,
      isFetchingPermissions: false,
    },
    errors: {
      fetchPermissionsError: null,
      authInfoProcessingError: null,
    },
    logout: jest.fn(),
    loginWithEmailAndPassword: jest.fn(),
    loginWithProvider: jest.fn(),
  };

  render(
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <IntlProvider locale="en" messages={messages.en}>
          <NotificationsProvider>
            <BreadcrumbsActionsProvider>
              <AuthContext.Provider value={newAuth}>{component}</AuthContext.Provider>
            </BreadcrumbsActionsProvider>
          </NotificationsProvider>
        </IntlProvider>
      </QueryClientProvider>
    </ThemeProvider>,
  );
};
export const setupDom = (): HTMLDivElement => {
  const container = document.createElement('div');
  container.setAttribute('id', 'root');
  document.body.append(container);
  return container;
};

export const cleanupDom = (container: HTMLDivElement | null): void => {
  if (container) {
    unmountComponentAtNode(container);
    container.remove();
  }
};

export const renderWithProvidersAndAuthV1 = (
  component: ReactNode,
  authMock: AuthContextType,
): void => {
  const theme = createTheme();
  const queryClient = new QueryClient();

  render(
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <IntlProvider locale="en" messages={messages.en}>
          <NotificationsProvider>
            <BreadcrumbsActionsProvider>
              <AuthContext.Provider value={authMock}>{component}</AuthContext.Provider>
            </BreadcrumbsActionsProvider>
          </NotificationsProvider>
        </IntlProvider>
      </QueryClientProvider>
    </ThemeProvider>,
  );
};
